import type { GlAccount } from '../types/account';
import type { InventoryStartingValue } from '../types/inventoryStartingValue';
import { todayLocalIso } from './localDate';

export const BULK_STARTING_VALUE_MAX_LINES = 50;

export interface StartingValueLocalLine {
  item_id: number;
  item_name: string;
  item_sku: string;
  qty: string;
  unit_cost: string;
  memo: string;
}

export const todayIso = todayLocalIso;

export const formatStartingValueLabel = (value: string) =>
  value
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export const formatStartingValueDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString();

export const formatStartingValueTime = (dateString: string) =>
  new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

export const canViewStartingValue = () => {
  const role = localStorage.getItem('user_role')?.toUpperCase();
  return (
    role === 'ADMIN' ||
    role === 'ACCOUNTANT' ||
    role === 'MANAGER' ||
    role === 'CASHIER'
  );
};

export const canSetStartingValue = () => {
  const role = localStorage.getItem('user_role')?.toUpperCase();
  return role === 'ADMIN' || role === 'ACCOUNTANT';
};

export const canVoidStartingValue = () =>
  localStorage.getItem('user_role')?.toUpperCase() === 'ADMIN';

export const canCorrectStartingValue = () =>
  localStorage.getItem('user_role')?.toUpperCase() === 'ADMIN';

export const canEditStartingValue = () =>
  localStorage.getItem('user_role')?.toUpperCase() === 'ADMIN';

export const canShowStartingValueCostCorrection = (
  startingValue: InventoryStartingValue | null | undefined
) =>
  startingValue != null &&
  startingValue.status === 'OPEN' &&
  canCorrectStartingValue() &&
  (startingValue.available_actions?.includes('correct') ?? false);

export const canShowStartingValueEdit = (
  startingValue: InventoryStartingValue | null | undefined
) =>
  startingValue != null &&
  startingValue.status === 'OPEN' &&
  canEditStartingValue() &&
  (startingValue.available_actions?.includes('edit') ?? false);

export const getStartingValueStatusColor = (status: string) => {
  switch (status.toUpperCase()) {
    case 'OPEN':
      return 'bg-green-100 text-green-800';
    case 'VOIDED':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

export const parseStartingValueNumber = (value: string | number | undefined) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

export const lineTotalValue = (line: StartingValueLocalLine) => {
  const qty = parseStartingValueNumber(line.qty);
  const unitCost = parseStartingValueNumber(line.unit_cost);
  return qty * unitCost;
};

export const itemHasZeroQty = (qtyOnHand: string | number | undefined) =>
  parseStartingValueNumber(qtyOnHand) === 0;

export const defaultEquityAccountId = (accounts: GlAccount[]): string => {
  const equityAccounts = accounts.filter((a) => a.active && a.account_type === 'EQUITY');
  const ownerEquity = equityAccounts.find((a) => a.account_number === '3000');
  if (ownerEquity) {
    return String(ownerEquity.id);
  }
  if (equityAccounts.length > 0) {
    return String(equityAccounts[0].id);
  }
  return '';
};

export const emptyStartingValueLine = (): StartingValueLocalLine => ({
  item_id: 0,
  item_name: '',
  item_sku: '',
  qty: '',
  unit_cost: '',
  memo: '',
});
